import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Download, ExternalLink } from 'lucide-react';
import { profileAPI } from '../../services/api';
import { saveBlob } from '../../services/download';
import { PRIVACY_CONTACT_EMAIL, PRIVACY_NOTICE_URL } from '../../config/privacy';
import { AppButton } from '../ui/AppButton';
import { SectionCard } from '../ui/SectionCard';

/**
 * The person's GDPR rights over the data the Hub keeps about them: read how
 * it is processed, download it, correct it (Profile card, SSO account), and
 * ask for erasure. Erasure spans Keycloak, the Hub and the blockchain, so it
 * is a request rather than a button.
 */
export const PrivacyCard = () => {
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const blob = await profileAPI.exportMyData();
      saveBlob(`livebetter-hub-my-data-${new Date().toISOString().slice(0, 10)}.json`, blob);
    } catch (error) {
      toast.error(error?.message || 'Your data could not be downloaded.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <SectionCard title="Privacy and your data" subtitle="What the Hub keeps about you, and your rights over it.">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-start gap-1.5">
          <AppButton
            variant="secondary"
            icon={<Download size={14} aria-hidden="true" />}
            loading={downloading}
            onClick={download}
          >
            Download my data
          </AppButton>
          <p className="m-0 text-[12px] leading-[1.45] text-lb-muted">
            Your account, profile, roles, organisations, notifications and your own actions in the Hub, as a JSON file.
          </p>
        </div>

        <p className="m-0 text-[13px] leading-[1.5] text-lb-body">
          Correct your details in the profile above or in your SSO account. To have your account deleted,{' '}
          {PRIVACY_CONTACT_EMAIL ? (
            <>
              write to <span className="font-semibold text-lb-ink select-all">{PRIVACY_CONTACT_EMAIL}</span>
            </>
          ) : (
            'ask your Hub administrator'
          )}
          . Records already written to the blockchain, such as a wallet address on a data agreement, cannot be erased.
        </p>

        {PRIVACY_NOTICE_URL && (
          <a
            href={PRIVACY_NOTICE_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 self-start text-[13px] font-semibold text-lb-green-primary hover:underline rounded outline-none focus-ring"
          >
            Read the privacy notice
            <ExternalLink size={13} aria-hidden="true" />
          </a>
        )}
      </div>
    </SectionCard>
  );
};
